import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Workspace, WorkspaceInvitationPreview } from "@/api/types";
import { useAuth } from "@/features/auth/AuthContext";
import { workspaceKeys } from "@/features/workspaces/queries/keys";

const ACTIVE_WORKSPACE_KEY = "memoru_active_workspace";

interface WorkspaceContextValue {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  loading: boolean;
  ready: boolean;
  creating: boolean;
  error: string | null;
  selectWorkspace: (workspaceId: string) => void;
  createTeam: (name: string) => Promise<Workspace>;
  acceptInvitation: (token: string) => Promise<void>;
  previewInvitation: (token: string) => Promise<WorkspaceInvitationPreview>;
  reload: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

function readActiveWorkspaceId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_WORKSPACE_KEY);
  } catch {
    return null;
  }
}

function writeActiveWorkspaceId(id: string) {
  try {
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
  } catch {
    // Workspace selection is still usable for the current tab.
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { api, status } = useAuth();
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(readActiveWorkspaceId);
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: workspaceKeys.list(),
    queryFn: () => api.listWorkspaces(),
    enabled: status === "authenticated",
  });

  const workspaces = useMemo(
    () => (status === "authenticated" ? (listQuery.data ?? []) : []),
    [listQuery.data, status],
  );

  useEffect(() => {
    if (status !== "authenticated") {
      setActiveId(null);
      return;
    }
    if (!listQuery.data?.length) return;
    const preferred = listQuery.data.find((workspace) => workspace.id === activeId);
    const fallback = preferred ?? listQuery.data[0] ?? null;
    if (fallback && fallback.id !== activeId) {
      setActiveId(fallback.id);
      writeActiveWorkspaceId(fallback.id);
    }
  }, [activeId, listQuery.data, status]);

  const selectWorkspace = useCallback((workspaceId: string) => {
    setActiveId(workspaceId);
    writeActiveWorkspaceId(workspaceId);
  }, []);

  const reload = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: workspaceKeys.list() });
  }, [queryClient]);

  const createTeam = useCallback(async (name: string) => {
    setCreating(true);
    try {
      const created = await api.createWorkspace(name.trim());
      await queryClient.invalidateQueries({ queryKey: workspaceKeys.list() });
      setActiveId(created.id);
      writeActiveWorkspaceId(created.id);
      setActionError(null);
      return created;
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : "Could not create workspace.";
      setActionError(message);
      throw nextError;
    } finally {
      setCreating(false);
    }
  }, [api, queryClient]);

  const acceptInvitation = useCallback(async (token: string) => {
    await api.acceptWorkspaceInvitation(token.trim());
    const next = await api.listWorkspaces();
    queryClient.setQueryData(workspaceKeys.list(), next);
    const joined = next.find((workspace) => workspace.type === "team" && workspace.id !== activeId);
    const fallback = joined ?? next.find((workspace) => workspace.id === activeId) ?? next[0] ?? null;
    setActiveId(fallback?.id ?? null);
    if (fallback) writeActiveWorkspaceId(fallback.id);
    setActionError(null);
  }, [activeId, api, queryClient]);

  const previewInvitation = useCallback((token: string) => (
    api.previewWorkspaceInvitation(token.trim())
  ), [api]);

  const currentWorkspace = workspaces.find((workspace) => workspace.id === activeId) ?? null;
  const ready = status !== "authenticated" || listQuery.isFetched;
  const value = useMemo<WorkspaceContextValue>(() => ({
    workspaces,
    currentWorkspace,
    loading: status === "authenticated" && listQuery.isLoading,
    ready,
    creating,
    error: actionError ?? (listQuery.error instanceof Error ? listQuery.error.message : null),
    selectWorkspace,
    createTeam,
    acceptInvitation,
    previewInvitation,
    reload,
  }), [
    workspaces,
    currentWorkspace,
    ready,
    status,
    listQuery.isLoading,
    listQuery.error,
    creating,
    actionError,
    selectWorkspace,
    createTeam,
    acceptInvitation,
    previewInvitation,
    reload,
  ]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error("useWorkspace must be used within WorkspaceProvider");
  return context;
}
